export type PlanId = 'free' | 'starter' | 'pro' | 'studio'
export type Currency = 'USD' | 'BRL' | 'EUR'
export type BillingCycle = 'monthly' | 'annual'
export const founderOfferCode = 'founder_2026' as const

export interface PricingData {
  usd: number
  brl: number
  eur: number
}

export const monthlyPricing: Record<PlanId, PricingData> = {
  free: { usd: 0, brl: 0, eur: 0 },
  starter: { usd: 9, brl: 49, eur: 9 },
  pro: { usd: 19, brl: 89, eur: 19 },
  studio: { usd: 39, brl: 149, eur: 39 }
}

export const annualPricing: Record<PlanId, PricingData> = {
  free: { usd: 0, brl: 0, eur: 0 },
  starter: { usd: 90, brl: 490, eur: 90 },
  pro: { usd: 190, brl: 890, eur: 190 },
  studio: { usd: 390, brl: 1490, eur: 390 }
}

export const founderPricing: Record<Exclude<PlanId, 'free'>, PricingData> = {
  starter: { usd: 79, brl: 436, eur: 79 },
  pro: { usd: 169, brl: 797, eur: 169 },
  studio: { usd: 349, brl: 1337, eur: 349 },
}

export const pricing: Record<PlanId, PricingData> = monthlyPricing

export const extraSeatPricing: PricingData = {
  usd: 5,
  brl: 25,
  eur: 5
}

export interface FeatureAvailability {
  free: boolean | string
  starter: boolean | string
  pro: boolean | string
  studio: boolean | string
}

export const getAnnualSavingsPercent = (): number => {
  return 17
}

export const getPriceAmount = (pricingData: PricingData, currency: Currency): number => {
  if (currency === 'USD') return pricingData.usd
  if (currency === 'EUR') return pricingData.eur
  return pricingData.brl
}

export const getAnnualAnchorPrice = (planId: Exclude<PlanId, 'free'>, currency: Currency): number => {
  return getPriceAmount(monthlyPricing[planId], currency) * 12
}

export const getFounderAnnualPrice = (planId: Exclude<PlanId, 'free'>, currency: Currency): number => {
  return getPriceAmount(founderPricing[planId], currency)
}

export const getFounderSavingsPercent = (planId: Exclude<PlanId, 'free'>, currency: Currency): number => {
  const anchor = getAnnualAnchorPrice(planId, currency)
  const founderAnnual = getFounderAnnualPrice(planId, currency)

  if (anchor <= 0) return 0

  return Number((((anchor - founderAnnual) / anchor) * 100).toFixed(1))
}

export const shouldShowFounderPricing = (offerActive: boolean, isFounder: boolean): boolean => {
  return offerActive || isFounder
}

export const resolveFounderOfferCode = (params: {
  offerActive: boolean
  isFounder: boolean
}): typeof founderOfferCode | undefined => {
  return params.offerActive && !params.isFounder ? founderOfferCode : undefined
}

export const resolveFounderOfferCodeForBillingCycle = (params: {
  billingCycle: BillingCycle
  offerActive: boolean
  isFounder: boolean
}): typeof founderOfferCode | undefined => {
  if (params.billingCycle !== 'annual') {
    return undefined
  }

  return resolveFounderOfferCode({
    offerActive: params.offerActive,
    isFounder: params.isFounder,
  })
}

export const formatPrice = (amount: number, currency: Currency): string => {
  if (amount === 0) return '0'
  
  if (currency === 'USD') {
    return `$${amount}`
  }
  if (currency === 'EUR') {
    return `€${amount}`
  }
  return `R$${amount}`
}

export const getCurrencySymbol = (currency: Currency): string => {
  if (currency === 'USD') return '$'
  if (currency === 'EUR') return '€'
  return 'R$'
}

export const getPriceByBillingCycle = (planId: PlanId, billingCycle: BillingCycle): PricingData => {
  return billingCycle === 'annual' ? annualPricing[planId] : monthlyPricing[planId]
}
